import React, { useState, useCallback, memo } from "react";
import { Card } from "reactstrap";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { IProduct } from "../../interfaces/products";
import { PRODUCT_TYPE } from "../../interfaces/products";
import { INotification } from "../../interfaces/notification";
import { routes } from "../../routes/routes";
import config from "../../utils/config";
import { getProductImagePlaceholder } from "../../utils/ingredient";
import ReactImageFallback from "react-image-fallback";
import { ImageSkeleton } from "../../utils/ghostUI";
import { Bell, PlusSquare, Bookmark } from "react-feather";

// Simplified product interface for ProductCard
export interface ProductCardData {
    id: string;
    name: string;
    version?: number;
    image_uri?: string;
    brand?: { name?: string };
    manufacturer?: { name?: string };
    country?: { alpha_2?: string };
    has_recommendations?: boolean;
    recommendations_count?: number;
    price?: number;
}

export interface ProductCardProps extends RouteComponentProps {
    product: ProductCardData;
    productType: PRODUCT_TYPE;
    variant?: 'default' | 'search' | 'dashboard' | 'compact';
    showActions?: boolean;
    onAddToPortfolio?: (id: string) => Promise<any>;
    onRemoveFromPortfolio?: (id: string) => Promise<any>;
    onShowProductActions?: () => void;
    notification?: INotification[];
    className?: string;
    style?: React.CSSProperties;
}

const ProductCard: React.FC<ProductCardProps> = memo(({
    product,
    productType,
    variant = 'default',
    showActions = true,
    onAddToPortfolio,
    onRemoveFromPortfolio,
    onShowProductActions,
    notification,
    history,
    className = '',
    style = {}
}) => {
    const [isLoading, setIsLoading] = useState(false);

    const handleCardClick = useCallback(() => {
        history.push(`${routes.PRODUCTS}/${product.id}`);
    }, [history, product.id]);

    const handleActionClick = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (onShowProductActions) {
            onShowProductActions();
        }
    }, [onShowProductActions]);

    const handleAddToPortfolio = useCallback(async (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (onAddToPortfolio) {
            setIsLoading(true);
            try {
                await onAddToPortfolio(product.id);
            } finally {
                setIsLoading(false);
            }
        }
    }, [onAddToPortfolio, product.id]);

    const handleRemoveFromPortfolio = useCallback(async (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (onRemoveFromPortfolio) {
            setIsLoading(true);
            try {
                await onRemoveFromPortfolio(product.id);
            } finally {
                setIsLoading(false);
            }
        }
    }, [onRemoveFromPortfolio, product.id]);

    const getCardClasses = () => {
        const baseClasses = 'product-card';
        const variantClasses = {
            default: 'product-card--default',
            search: 'product-card--search',
            dashboard: 'product-card--dashboard',
            compact: 'product-card--compact'
        };
        return `${baseClasses} ${variantClasses[variant]} ${className}`.trim();
    };

    const getImageUrl = () => {
        if (product.image_uri) {
            return product.image_uri;
        }
        // For products without image_uri, return null to trigger fallback to local placeholder
        // instead of using ingredient API which shows "PHOTO COMING SOON" placeholders
        return null;
    };

    const renderActions = () => {
        if (!showActions) return null;


        switch (variant) {
            case 'search':
                return (
                    <div className="product-card__actions">
                        {onAddToPortfolio && (
                            <button
                                className="product-card__action-btn product-card__action-btn--add"
                                onClick={handleAddToPortfolio}
                                disabled={isLoading}
                                aria-label="Add to portfolio"
                            >
                                <PlusSquare size={18} />
                            </button>
                        )}
                    </div>
                );

            case 'dashboard':
                return (
                    <div className="product-card__actions">
                        <button
                            className="product-card__action-btn product-card__action-btn--create"
                            onClick={handleActionClick}
                            aria-label="Create new product"
                        >
                            <span>Create new product</span>
                            <PlusSquare size={20} />
                        </button>
                    </div>
                );

            default:
                return (
                    <div className="product-card__actions">
                        <button
                            className="product-card__action-btn product-card__action-btn--notify"
                            onClick={handleActionClick}
                            aria-label="Get recommendations"
                        >
                            <span>Try {product.name}</span>
                            <Bell size={20} />
                        </button>
                    </div>
                );
        }
    };

    const renderBadges = () => {
        const badges = [];

        if (product.has_recommendations || (product.recommendations_count && product.recommendations_count > 0)) {
            badges.push(
                <div key="ai-badge" className="product-card__badge product-card__badge--ai">
                    <img
                        src={require("../../assets/images/JourneyAI_purple.png")}
                        alt="AI Recommended"
                        className="product-card__badge-icon"
                    />
                </div>
            );
        }

        if (product.country?.alpha_2) {
            badges.push(
                <div key="country-badge" className="product-card__badge product-card__badge--country">
                    <i className={`${product.country.alpha_2.toLowerCase()} flag list-flag`}></i>
                </div>
            );
        }

        return badges;
    };

    return (
        <Card
            className={getCardClasses()}
            onClick={handleCardClick}
            style={style}
        >
            <div className="product-card__image-container">
                <ReactImageFallback
                    src={getImageUrl() || getProductImagePlaceholder()}
                    fallbackImage={getProductImagePlaceholder()}
                    alt={`${product.name} product image`}
                    className="product-card__image"
                    initialImage={<ImageSkeleton className="product-card__skeleton" />}
                />
                {renderBadges()}
            </div>

            <div className="product-card__content">
                <div className="product-card__header">
                    <h3 className="product-card__title">{product.name}</h3>
                    <p className="product-card__subtitle">
                        {product.brand?.name || product.manufacturer?.name || 'Unknown Brand'}
                    </p>
                    <div className="product-card__meta">
                        <span className="product-card__version">v{product.version}</span>
                        <span className="product-card__type">{productType}</span>
                    </div>
                </div>

                {renderActions()}
            </div>
        </Card>
    );
});

ProductCard.displayName = 'ProductCard';

export default withRouter(ProductCard);
